import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, orderBy, where, doc, onSnapshot } from 'firebase/firestore';
import { ref, onDisconnect, set, serverTimestamp } from "firebase/database";
import { useAppContext } from '../AppContext';
import useFirestoreQuery from '../hooks/useFirestoreQuery';
import CommentThread from './CommentThread';
import CommentForm from './CommentForm';

const CommentSection = ({ entryId, contentType = 'whisper', nexusId = null, initialContent = '', topComment = null }) => {
  const { t } = useTranslation();
  const { db, rtdb, appId, userId, userProfiles } = useAppContext();
  const [commentsCount, setCommentsCount] = useState(0);
  const [deletedCommentIds, setDeletedCommentIds] = useState([]);

  const basePath = useMemo(() => {
    if (contentType === 'moment') {
        return `artifacts/${appId}/public/data/moments/${entryId}`;
    } else if (contentType === 'nexusPost' && nexusId) {
        return `artifacts/${appId}/public/data/nexuses/${nexusId}/posts/${entryId}`;
    } else {
        return `artifacts/${appId}/public/data/whispers/${entryId}`;
    }
  }, [appId, entryId, contentType, nexusId]);

  useEffect(() => {
    const contentRef = doc(db, basePath);
    const unsubscribe = onSnapshot(contentRef, (docSnap) => {
        setCommentsCount(docSnap.data()?.commentsCount || 0);
    });
    return () => unsubscribe();
  }, [db, basePath]);

  useEffect(() => {
    if (!rtdb || !userId || !entryId) return;

    const presenceRef = ref(rtdb, `whisper_presence/${entryId}/${userId}`);
    
    set(presenceRef, serverTimestamp()).catch(err => console.error("Failed to set presence:", err));

    const disconnectRef = onDisconnect(presenceRef);
    disconnectRef.remove().catch(err => console.error("Failed to set onDisconnect:", err));

    return () => {
      disconnectRef.cancel(); // Important to cancel onDisconnect when component unmounts cleanly
      set(presenceRef, null).catch(err => console.error("Failed to remove presence on unmount:", err));
    };
  }, [rtdb, userId, entryId]);

  // Query for top-level comments
  const commentsQuery = useMemo(() =>
    query(
      collection(db, `${basePath}/comments`),
      where("parentId", "==", null),
      orderBy("timestamp", "asc")
    ),
    [db, basePath]
  );

  const { data: comments, isLoading, error } = useFirestoreQuery(commentsQuery);

  const handleCommentDeleted = (commentId) => {
    setDeletedCommentIds(prev => [...prev, commentId]);
  };

  const filteredComments = useMemo(() => {
    return comments.filter(comment => !deletedCommentIds.includes(comment.id));
  }, [comments, deletedCommentIds]);

  if (isLoading) {
    return <p>{t('commentSection.loading')}</p>;
  }

  if (error) {
    return <p>{t('commentSection.error')}</p>;
  }

  return (
    <div className="mt-6 pt-6 border-t border-gray-700/50">
        <h4 className="text-xl font-semibold text-gray-100 font-playfair mb-4">{t('commentSection.conversation', { count: commentsCount })}</h4>
        <CommentForm entryId={entryId} contentType={contentType} nexusId={nexusId} initialContent={initialContent} parentPath={basePath} />
        <div className="mt-4 space-y-4">
            {topComment && !filteredComments.some(c => c.id === topComment.id) && (
                <div key={topComment.id} className="comment-thread p-2 border border-yellow-400/30 rounded-lg bg-yellow-900/10">
                    <p className="text-xs font-semibold text-yellow-300 mb-2 ml-2">{t('commentSection.topComment')}</p>
                    <CommentThread comment={topComment} entryId={entryId} userProfiles={userProfiles} contentType={contentType} nexusId={nexusId} onCommentDeleted={handleCommentDeleted} nestingLevel={0} />
                </div>
            )}
            {filteredComments.map(comment => (
              <div key={comment.id} className="comment-thread">
                <CommentThread comment={comment} entryId={entryId} userProfiles={userProfiles} contentType={contentType} nexusId={nexusId} onCommentDeleted={handleCommentDeleted} nestingLevel={0} />
              </div>
            ))}
        </div>
    </div>
  );
};

export default CommentSection;